import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import { FaCalendar, FaRedo, FaEye, FaExclamationTriangle, FaDownload } from 'react-icons/fa';
import SuperAdminSidebar from '../../components/SuperAdmin/SuperAdminSidebar';
import { API_ENDPOINTS } from '../../config/api';
import '../../styles/Dashboard.css';
import '../../styles/SuperAdmin/SubscriptionsManagement.css';

const SubscriptionsManagement = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const [companies, setCompanies] = useState([]);
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('active');
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [viewingInvoice, setViewingInvoice] = useState(null);

  const isNearExpiry = useCallback((subscription) => {
    const days = Number(subscription?.daysRemaining ?? 0);
    return days > 0 && days <= 30 && subscription?.status === 'active';
  }, []);

  const isRenewal = useCallback((subscription) => {
    if (subscription?.status !== 'active') return false;
    return Boolean(subscription?.upcomingRenewal) || isNearExpiry(subscription);
  }, [isNearExpiry]);

  const isExpired = useCallback((subscription) => {
    const days = Number(subscription?.daysRemaining ?? 0);
    return subscription?.status === 'expired' || days <= 0;
  }, []);

  const fetchSubscriptions = useCallback(async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await fetch(API_ENDPOINTS.SUBSCRIPTIONS, {
        headers: {
          Authorization: token ? `Bearer ${token}` : ''
        }
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch subscriptions: ${response.status}`);
      }
      
      const data = await response.json();
    
      setSubscriptions(Array.isArray(data) ? data : []);
    } catch (error) {
   
      setFetchError('Unable to fetch subscriptions right now. Please check backend/API configuration.');
      setSubscriptions([]);
    }
  }, []);

  const fetchCompanies = useCallback(async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await fetch(API_ENDPOINTS.COMPANIES, {
        headers: {
          Authorization: token ? `Bearer ${token}` : ''
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch companies: ${response.status}`);
      }

      const data = await response.json();
      setCompanies(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Error fetching companies:', error);
      setFetchError('Unable to fetch companies right now. Please check backend/API configuration.');
      setCompanies([]);
    }
  }, []);

  const fetchInvoices = useCallback(async () => {
    try {
      const token = sessionStorage.getItem('token');
      const response = await fetch(API_ENDPOINTS.LAB_ADMIN_INVOICES, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch invoices: ${response.status}`);
      }

      const payload = await response.json();
      const data = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload)
          ? payload
          : [];
      setInvoices(data);
    } catch (error) {
      console.error('Error fetching invoices:', error);
      setInvoices([]);
    }
  }, []);

  const formatDate = (value) => {
    if (!value) return '-';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleDateString();
  };

  const calculateDueDate = (purchasedOn, billingCycle) => {
    if (!purchasedOn) return null;
    const purchased = new Date(purchasedOn);
    if (Number.isNaN(purchased.getTime())) return null;

    const dueDate = new Date(purchased);
    const normalizedCycle = String(billingCycle || '').toLowerCase().trim();
    if (normalizedCycle === 'monthly') {
      dueDate.setMonth(dueDate.getMonth() + 1);
    } else if (normalizedCycle === 'quarterly') {
      dueDate.setMonth(dueDate.getMonth() + 3);
    } else if (normalizedCycle === 'yearly' || normalizedCycle === 'annual') {
      dueDate.setFullYear(dueDate.getFullYear() + 1);
    } else {
      dueDate.setDate(dueDate.getDate() + 30);
    }

    return dueDate;
  };

  const generateInvoicePDF = (invoice, shouldSave = true) => {
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const pageWidth = doc.internal.pageSize.getWidth();
    const sideMargin = 40;
    const contentWidth = pageWidth - sideMargin * 2;
    const currency = 'INR';

    const formatAmount = (value) => `${currency} ${Number(value || 0).toFixed(2)}`;
    const safeText = (value) => String(value ?? '-');

    const customerName = invoice.customer?.companyName || invoice.customer?.contactPerson || '-';
    const customerAddressLines = [
      invoice.customer?.contactPerson ? `Contact: ${invoice.customer.contactPerson}` : null,
      invoice.customer?.email ? `Email: ${invoice.customer.email}` : null,
      invoice.customer?.phone ? `Phone: ${invoice.customer.phone}` : null
    ].filter(Boolean);

    const invoiceNumber = safeText(invoice.invoiceNumber || 'INV-000001');
    const invoiceDate = formatDate(invoice.purchasedOn);
    const dueDate = formatDate(calculateDueDate(invoice.purchasedOn, invoice.billingCycle));
    const billingCycle = safeText((invoice.billingCycle || '-').toUpperCase());
    const qty = 1;
    const rate = Number(invoice.amount || 0);
    const subTotal = rate * qty;
    const taxRate = Number(invoice.taxRate || 0);
    const taxAmount = (subTotal * taxRate) / 100;
    const totalAmount = subTotal + taxAmount;

    let currentY = 40;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.setTextColor(30, 30, 30);
    doc.text('AssetFlows', sideMargin, currentY);

    doc.setFont('helvetica');
    doc.setFontSize(11);
    doc.setTextColor(80, 80, 80);
    currentY += 24;
    doc.text('AssetFlows Technologies Pvt. Ltd.', sideMargin, currentY);
    currentY += 14;
    doc.text('Innovation Park, Pune, Maharashtra 411014', sideMargin, currentY);
    currentY += 14;
    doc.text('Email: billing@assetflow.in | Website: www.assetflow.in', sideMargin, currentY);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(28);
    doc.setTextColor(25, 74, 160);
    doc.text('INVOICE', sideMargin + contentWidth, currentY - 38, { align: 'right' });

    currentY += 28;
    doc.setDrawColor(200, 200, 200);
    doc.line(sideMargin, currentY, sideMargin + contentWidth, currentY);
    currentY += 16;

    const col1 = sideMargin;
    const col2 = sideMargin + contentWidth * 0.25;
    const col3 = sideMargin + contentWidth * 0.5;
    const col4 = sideMargin + contentWidth * 0.75;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    doc.text('Invoice No.', col1, currentY);
    doc.text('Issue Date', col2, currentY);
    doc.text('Due Date', col3, currentY);
    doc.text('Billing Cycle', col4, currentY);

    currentY += 14;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.setTextColor(30, 30, 30);
    doc.text(invoiceNumber, col1, currentY);
    doc.text(invoiceDate, col2, currentY);
    doc.text(dueDate, col3, currentY);
    doc.text(billingCycle, col4, currentY);

    currentY += 20;
    doc.setDrawColor(200, 200, 200);
    doc.line(sideMargin, currentY, sideMargin + contentWidth, currentY);
    currentY += 16;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('BILL TO', sideMargin, currentY);

    currentY += 16;
    doc.setFontSize(12);
    doc.text(customerName, sideMargin, currentY);

    currentY += 14;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    customerAddressLines.forEach((line) => {
      doc.text(line, sideMargin, currentY);
      currentY += 12;
    });

    currentY += 8;
    doc.setFontSize(10);
    doc.text('Status:', sideMargin, currentY);
    doc.text(safeText(invoice.status || 'ongoing').toUpperCase(), sideMargin + 40, currentY);
    doc.text('Payment Gateway:', col2, currentY);
    doc.text(safeText(invoice.paymentGateway || 'Razorpay / Manual'), col2 + 110, currentY);

    currentY += 20;
    doc.setDrawColor(200, 200, 200);
    doc.line(sideMargin, currentY, sideMargin + contentWidth, currentY);
    currentY += 14;

    doc.setFillColor(25, 74, 160);
    doc.rect(sideMargin, currentY, contentWidth, 20, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(255, 255, 255);

    const tableCol1 = sideMargin + 10;
    const tableCol3 = sideMargin + contentWidth - 150;
    const tableCol4 = sideMargin + contentWidth - 100;
    const tableCol5 = sideMargin + contentWidth - 40;

    doc.text('Description', tableCol1, currentY + 13);
    doc.text('Quantity', tableCol3, currentY + 13);
    doc.text('Unit Price', tableCol4, currentY + 13, { align: 'right' });
    doc.text('Amount', tableCol5, currentY + 13, { align: 'right' });

    currentY += 24;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(30, 30, 30);
    doc.setDrawColor(220, 220, 220);
    doc.rect(sideMargin, currentY, contentWidth, 32);

    doc.text(safeText(invoice.planName || 'Subscription Plan'), tableCol1, currentY + 12);
    doc.setFontSize(9);
    doc.setTextColor(100, 100, 100);
    doc.text(
      `Plan access from ${invoiceDate} | Limits: ${safeText(invoice.limits?.users ?? '-')} users, ${safeText(invoice.limits?.assets ?? '-')} assets`,
      tableCol1,
      currentY + 24
    );

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(30, 30, 30);
    doc.text(String(qty), tableCol3, currentY + 12, { align: 'center' });
    doc.text(formatAmount(rate), tableCol4, currentY + 12, { align: 'right' });
    doc.text(formatAmount(subTotal), tableCol5, currentY + 12, { align: 'right' });

    currentY += 36;
    const totalsLabelX = sideMargin + contentWidth - 160;
    const totalsValueX = sideMargin + contentWidth;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    doc.text('Subtotal', totalsLabelX, currentY);
    doc.setFont('helvetica', 'bold');
    doc.text(formatAmount(subTotal), totalsValueX, currentY, { align: 'right' });

    currentY += 14;
    doc.setFont('helvetica', 'normal');
    doc.text(`Tax (${taxRate.toFixed(2)}%)`, totalsLabelX, currentY);
    doc.setFont('helvetica', 'bold');
    doc.text(formatAmount(taxAmount), totalsValueX, currentY, { align: 'right' });

    currentY += 18;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(25, 74, 160);
    doc.text('Total Paid', totalsLabelX, currentY);
    doc.text(formatAmount(totalAmount), totalsValueX, currentY, { align: 'right' });

    if (shouldSave) {
      doc.save(`${invoice.invoiceNumber || 'invoice'}.pdf`);
    }

    return doc;
  };

  const handleDownloadInvoice = (invoice) => {
    generateInvoicePDF(invoice, true);
  };

  const handleViewInvoice = (invoice) => {
    const doc = generateInvoicePDF(invoice, false);
    const pdfDataUrl = doc.output('dataurlstring');
    setViewingInvoice({ ...invoice, pdfDataUrl });
  };

  const findInvoiceForSubscription = useCallback((subscription) => {
    if (!subscription) return null;

    const subscriptionId = String(subscription.subscriptionId || '').trim();
    const companyId = String(subscription.companyId?._id || subscription.companyId || '').trim();
    const companyName = String(subscription.companyId?.name || subscription.company || '').trim().toLowerCase();

    if (subscriptionId) {
      const byInvoiceNumber = invoices.find((invoice) => String(invoice.invoiceNumber || '').trim() === subscriptionId);
      if (byInvoiceNumber) return byInvoiceNumber;
    }

    if (companyId) {
      const byCompanyId = invoices.find((invoice) => String(invoice.companyId || '').trim() === companyId);
      if (byCompanyId) return byCompanyId;
    }

    if (companyName) {
      return invoices.find((invoice) =>
        String(invoice.customer?.companyName || '').trim().toLowerCase() === companyName
      ) || null;
    }

    return null;
  }, [invoices]);

  const getCompanyKey = useCallback((item) => {
    const byId = item?.companyId?._id || item?.companyId;
    if (byId) return String(byId).trim();
    const byName = item?.companyId?.name || item?.company || item?.name;
    return String(byName || '').trim().toLowerCase();
  }, []);

  const latestSubscriptionsByCompany = useMemo(() => {
    const map = new Map();

    subscriptions.forEach((subscription) => {
      const key = getCompanyKey(subscription);
      if (!key) return;

      const existing = map.get(key);
      const currentDate = new Date(subscription.renewalDate || subscription.updatedAt || subscription.createdAt || 0).getTime();
      const existingDate = existing
        ? new Date(existing.renewalDate || existing.updatedAt || existing.createdAt || 0).getTime()
        : -1;

      if (!existing || currentDate >= existingDate) {
        map.set(key, subscription);
      }
    });

    return map;
  }, [subscriptions, getCompanyKey]);

  const legacyActiveCompanyRows = useMemo(() => {
    const subscribedKeys = new Set(
      subscriptions
        .map((subscription) => getCompanyKey(subscription))
        .filter(Boolean)
    );

    return companies
      .filter((company) => {
        const key = getCompanyKey(company);
        const hasSubscriptionRecord = key ? subscribedKeys.has(key) : false;
        const hasPlanConfigured = Boolean(company?.plan || company?.planId);
        const isCompanyActive = String(company?.status || '').toLowerCase() === 'active';
        return !hasSubscriptionRecord && hasPlanConfigured && isCompanyActive;
      })
      .map((company) => ({
        _id: `legacy-active-${company._id || company.email || company.name}`,
        company: company.name,
        companyId: {
          _id: company._id,
          name: company.name,
          contactPerson: company.contactPerson,
          phone: company.phone,
        },
        plan: company?.planId?.name || company.plan || 'Starter',
        subscriptionId: `LEGACY-${String(company._id || '').slice(-6).toUpperCase() || 'PLAN'}`,
        billingCycle: 'monthly',
        renewalDate: company.expiryDate || null,
        daysRemaining: Number(company.daysRemaining ?? 0),
        status: 'active',
        autoRenew: false,
        users: company.users || 0,
        assets: company.assets || 0,
        isLegacyCompanyPlan: true,
      }));
  }, [companies, subscriptions, getCompanyKey]);

  const activeRows = useMemo(
    () => [
      ...Array.from(latestSubscriptionsByCompany.values()).filter((s) => s.status === 'active'),
      ...legacyActiveCompanyRows,
    ],
    [latestSubscriptionsByCompany, legacyActiveCompanyRows]
  );

  const nearExpiryRows = useMemo(
    () => Array.from(latestSubscriptionsByCompany.values()).filter((s) => isNearExpiry(s)),
    [latestSubscriptionsByCompany, isNearExpiry]
  );

  const expiredRows = useMemo(
    () => Array.from(latestSubscriptionsByCompany.values()).filter((s) => isExpired(s)),
    [latestSubscriptionsByCompany, isExpired]
  );

  const renewedOrUpgradedRows = useMemo(() => {
    const groups = new Map();

    subscriptions.forEach((subscription) => {
      const key = getCompanyKey(subscription);
      if (!key) return;

      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(subscription);
    });

    const result = [];
    groups.forEach((companySubscriptions) => {
      if (!companySubscriptions.length) return;

      const uniquePlans = new Set(companySubscriptions.map((s) => String(s?.plan || '').trim()).filter(Boolean));
      const hasRenewed = companySubscriptions.length > 1;
      const hasUpgraded = uniquePlans.size > 1;

      if (!hasRenewed && !hasUpgraded) return;

      const latest = [...companySubscriptions].sort((a, b) => {
        const aDate = new Date(a.renewalDate || a.updatedAt || a.createdAt || 0).getTime();
        const bDate = new Date(b.renewalDate || b.updatedAt || b.createdAt || 0).getTime();
        return bDate - aDate;
      })[0];

      result.push({
        ...latest,
        isRenewedOrUpgraded: true,
      });
    });

    return result;
  }, [subscriptions, getCompanyKey]);

  const noPlanRegisteredRows = useMemo(() => {
    const subscribedKeys = new Set(
      subscriptions
        .map((subscription) => getCompanyKey(subscription))
        .filter(Boolean)
    );

    return companies
      .filter((company) => {
        const key = getCompanyKey(company);
        const hasSubscriptionRecord = key ? subscribedKeys.has(key) : false;
        const hasPlanConfigured = Boolean(company?.plan || company?.planId);
        return !hasSubscriptionRecord && !hasPlanConfigured;
      })
      .map((company) => ({
        _id: `no-plan-${company._id || company.email || company.name}`,
        company: company.name,
        companyId: {
          _id: company._id,
          name: company.name,
          contactPerson: company.contactPerson,
          phone: company.phone,
        },
        plan: 'No Plan',
        subscriptionId: '-',
        billingCycle: '-',
        renewalDate: null,
        daysRemaining: 0,
        status: 'pending',
        autoRenew: false,
        users: company.users || 0,
        assets: company.assets || 0,
        isNoPlanRegistered: true,
      }));
  }, [companies, subscriptions, getCompanyKey]);

  const tabRows = useMemo(() => {
  
    if (activeTab === 'active') return activeRows;
    if (activeTab === 'near-expire') return nearExpiryRows;
    if (activeTab === 'expired') return expiredRows;
    if (activeTab === 'no-plan') return noPlanRegisteredRows;
    if (activeTab === 'renewed-upgraded') return renewedOrUpgradedRows;
    return activeRows;
  }, [activeTab, activeRows, nearExpiryRows, expiredRows, noPlanRegisteredRows, renewedOrUpgradedRows]);

  const filteredSubscriptions = useMemo(() => {
    let filtered = tabRows;

    if (searchTerm) {
      const key = searchTerm.toLowerCase();
      filtered = filtered.filter((s) => {
        const companyName = s?.companyId?.name || s?.company || '';
        const contactPerson = s?.companyId?.contactPerson || '';
        const contactPhone = s?.companyId?.phone || '';
        const subscriptionId = s?.subscriptionId || '';
        const plan = s?.plan || '';

        return [companyName, contactPerson, contactPhone, subscriptionId, plan]
          .some((value) => String(value).toLowerCase().includes(key));
      });
    }

    return filtered;
  }, [tabRows, searchTerm]);

  const isValidTab = useCallback((value) => {
    return [ 'active', 'near-expire', 'expired', 'no-plan', 'renewed-upgraded'].includes(value);
  }, []);

  const setActiveTabAndSyncUrl = useCallback((nextTab) => {
    setActiveTab(nextTab);
    const params = new URLSearchParams(location.search);
    params.set('tab', nextTab);
    navigate(`/super-admin/subscriptions?${params.toString()}`, { replace: true });
  }, [location.search, navigate]);

  useEffect(() => {
    if (user.role !== 'super_admin') {
      navigate('/');
      return;
    }

    const loadData = async () => {
      setLoading(true);
      setFetchError('');
      await Promise.all([fetchSubscriptions(), fetchCompanies(), fetchInvoices()]);
      setLoading(false);
    };

    loadData();
  }, [user.role, navigate, fetchSubscriptions, fetchCompanies, fetchInvoices]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabFromQuery = params.get('tab');
    if (tabFromQuery && isValidTab(tabFromQuery)) {
      setActiveTab(tabFromQuery);
    }
  }, [location.search, isValidTab]);

  const handleViewDetails = (subscription) => {
    setSelectedSubscription(subscription);
    setShowDetailsModal(true);
  };

  const handleRenew = (subscriptionId) => {
    alert(`Renewal process initiated for subscription ${subscriptionId}`);
    setShowDetailsModal(false);
  };

  const getStatusColor = (status) => {
    if (status === 'active') return 'active';
    if (status === 'pending') return 'pending';
    return 'expired';
  };

  if (loading) {
    return (
      <div className="dashboard-wrapper">
        <SuperAdminSidebar user={user} />
        <div className="dashboard-main">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', fontSize: '16px', color: '#666' }}>Loading subscriptions...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-wrapper">
      <SuperAdminSidebar user={user} />
      
      <div className="dashboard-main">
        <div style={{ marginBottom: '30px' }}>
          <h1 style={{ margin: '0 0 8px 0', color: '#1a1f3a', fontSize: '28px', fontWeight: '700' }}>Subscriptions Management</h1>
          <p style={{ margin: '0', color: '#666', fontSize: '14px' }}>Track and manage all active subscriptions and renewal dates</p>
          {!!fetchError && (
            <p style={{ margin: '10px 0 0 0', color: '#b91c1c', fontSize: '13px', fontWeight: '600' }}>{fetchError}</p>
          )}
        </div>

        {/* Statistics Cards */}
        

        {/* Filters */}
        <div className="filters-section">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search by company, contact person, phone, subscription ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
         
        </div>

        <div className="subscriptions-tabs">
       
          <button
            className={`subscriptions-tab ${activeTab === 'active' ? 'active' : ''}`}
            onClick={() => setActiveTabAndSyncUrl('active')}
          >
            Active Subscribers ({activeRows.length})
          </button>
          <button
            className={`subscriptions-tab ${activeTab === 'near-expire' ? 'active' : ''}`}
            onClick={() => setActiveTabAndSyncUrl('near-expire')}
          >
            Near to Expire ({nearExpiryRows.length})
          </button>
          <button
            className={`subscriptions-tab ${activeTab === 'expired' ? 'active' : ''}`}
            onClick={() => setActiveTabAndSyncUrl('expired')}
          >
            Expired Plans ({expiredRows.length})
          </button>
          <button
            className={`subscriptions-tab ${activeTab === 'no-plan' ? 'active' : ''}`}
            onClick={() => setActiveTabAndSyncUrl('no-plan')}
          >
            Registered, No Plan ({noPlanRegisteredRows.length})
          </button>
          <button
            className={`subscriptions-tab ${activeTab === 'renewed-upgraded' ? 'active' : ''}`}
            onClick={() => setActiveTabAndSyncUrl('renewed-upgraded')}
          >
            Renewed / Upgraded ({renewedOrUpgradedRows.length})
          </button>
        </div>

        {/* Subscriptions Table */}
        <div className="subscriptions-section">
          {filteredSubscriptions.length === 0 ? (
            <div className="empty-state">
              <FaCalendar />
              <p>No subscriptions found</p>
            </div>
          ) : (
            <div className="table-wrapper">
              <table className="subscriptions-table">
                <thead>
                  <tr>
                    <th>Company</th>
                    <th>Contact Person / Admin</th>
                    <th>Contact Number</th>
                    <th>Subscription ID</th>
                    <th>Plan</th>
                    <th>Renewal Date</th>
                    <th>Status</th>
                    
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSubscriptions.map(subscription => {
                    const linkedInvoice = findInvoiceForSubscription(subscription);

                    return (
                    <tr
                      key={subscription._id || subscription.id}
                      className={`status-${getStatusColor(subscription.status)} ${isRenewal(subscription) ? 'upcoming' : ''}`}
                    >
                      <td>
                        <div className="company-cell">
                          <strong>{subscription.companyId?.name || subscription.company}</strong>
                        </div>
                      </td>
                      <td>{subscription.companyId?.contactPerson || '-'}</td>
                      <td>{subscription.companyId?.phone || '-'}</td>
                      <td>
                        <span className="subscription-id">{subscription.subscriptionId}</span>
                      </td>
                      <td>
                        <div className="plan-badge">{subscription.plan}</div>
                      </td>
                      <td>
                        <div className={`renewal-info ${subscription.daysRemaining < 30 && subscription.daysRemaining > 0 ? 'warning' : subscription.daysRemaining <= 0 ? 'danger' : ''}`}>
                          {subscription.daysRemaining > 0 ? (
                            <>
                              <span>{new Date(subscription.renewalDate).toLocaleDateString()}</span>
                              <br />
                              <small>{subscription.daysRemaining} days</small>
                            </>
                          ) : (
                            <>
                              <span>
                                {subscription.renewalDate
                                  ? `Expired on ${new Date(subscription.renewalDate).toLocaleDateString()}`
                                  : 'Not purchased yet'}
                              </span>
                            </>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className={`status-badge status-${subscription.status}`}>
                          {subscription.status.toUpperCase()}
                        </span>
                      </td>
  
                      <td>
                        <div className="subscription-action-buttons">
                          <button
                            className="btn-view-details"
                            onClick={() => handleViewDetails(subscription)}
                            title="View details"
                          >
                            <FaEye /> Details
                          </button>
                          <button
                            className="btn-view-details invoice"
                            onClick={() => handleViewInvoice(linkedInvoice)}
                            disabled={!linkedInvoice || subscription.isNoPlanRegistered}
                            title={linkedInvoice ? 'View Invoice' : 'Invoice not available'}
                          >
                            <FaEye /> Invoice
                          </button>
                          <button
                            className="btn-view-details download"
                            onClick={() => handleDownloadInvoice(linkedInvoice)}
                            disabled={!linkedInvoice || subscription.isNoPlanRegistered}
                            title={linkedInvoice ? 'Download Invoice' : 'Invoice not available'}
                          >
                            <FaDownload /> Download
                          </button>
                        </div>
                      </td>
                    </tr>
                  )})}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Details Modal */}
        {showDetailsModal && selectedSubscription && (
          <div className="modal-overlay" onClick={() => setShowDetailsModal(false)}>
            <div className="modal-content details-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Subscription Details</h2>
                <button className="modal-close" onClick={() => setShowDetailsModal(false)}>×</button>
              </div>

              <div className="modal-body">
                <div className="details-section">
                  <h3>Company & Subscription Info</h3>
                  <div className="details-grid">
                    <div className="detail-item">
                      <span className="label">Company:</span>
                      <span className="value">{selectedSubscription.companyId?.name || selectedSubscription.company}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Contact Person / Admin:</span>
                      <span className="value">{selectedSubscription.companyId?.contactPerson || '-'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Contact Number:</span>
                      <span className="value">{selectedSubscription.companyId?.phone || '-'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Subscription ID:</span>
                      <span className="value">{selectedSubscription.subscriptionId}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Status:</span>
                      <span className={`status-badge status-${selectedSubscription.status}`}>
                        {selectedSubscription.status.toUpperCase()}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="details-section">
                  <h3>Plan Details</h3>
                  <div className="details-grid">
                    <div className="detail-item">
                      <span className="label">Plan:</span>
                      <span className="value plan-badge">{selectedSubscription.plan}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Price (Monthly):</span>
                      <span className="value">{selectedSubscription.price !== undefined ? `$${selectedSubscription.price}` : '-'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Billing Cycle:</span>
                      <span className="value">{selectedSubscription.billingCycle ? selectedSubscription.billingCycle.toUpperCase() : '-'}</span>
                    </div>
                  </div>
                </div>

                <div className="details-section">
                  <h3>Duration & Renewal</h3>
                  <div className="details-grid">
                    <div className="detail-item">
                      <span className="label">Start Date:</span>
                      <span className="value">{selectedSubscription.startDate ? new Date(selectedSubscription.startDate).toLocaleDateString() : '-'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Renewal Date:</span>
                      <span className={`value ${selectedSubscription.daysRemaining < 30 && selectedSubscription.daysRemaining > 0 ? 'warning' : selectedSubscription.daysRemaining <= 0 ? 'danger' : ''}`}>
                        {selectedSubscription.renewalDate ? new Date(selectedSubscription.renewalDate).toLocaleDateString() : '-'}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Days Remaining:</span>
                      <span className={`value ${selectedSubscription.daysRemaining < 30 && selectedSubscription.daysRemaining > 0 ? 'warning' : selectedSubscription.daysRemaining <= 0 ? 'danger' : ''}`}>
                        {selectedSubscription.daysRemaining > 0 ? `${selectedSubscription.daysRemaining} days` : 'Expired'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="details-section">
                  <h3>License Details</h3>
                  <div className="details-grid">
                    <div className="detail-item">
                      <span className="label">Users Limit:</span>
                      <span className="value">{selectedSubscription.users}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Assets Limit:</span>
                      <span className="value">{selectedSubscription.assets}</span>
                    </div>
                    
                  </div>
                </div>

                {selectedSubscription.daysRemaining <= 30 && selectedSubscription.daysRemaining > 0 && (
                  <div className="alert-box warning">
                    <FaExclamationTriangle />
                    <span>This subscription expires in {selectedSubscription.daysRemaining} days. Consider sending a renewal reminder.</span>
                  </div>
                )}

                {selectedSubscription.daysRemaining <= 0 && (
                  <div className="alert-box danger">
                    <FaExclamationTriangle />
                    <span>This subscription has expired. The company will lose access to the service unless renewed.</span>
                  </div>
                )}

                <div className="modal-actions">
                  <button className="btn-renew" onClick={() => handleRenew(selectedSubscription.subscriptionId)}>
                    <FaRedo /> Renew Subscription
                  </button>
                  <button className="btn-cancel" onClick={() => setShowDetailsModal(false)}>
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {viewingInvoice && ReactDOM.createPortal(
        <div className="invoice-modal-overlay" onClick={() => setViewingInvoice(null)}>
          <div
            className="invoice-modal-shell"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="invoice-modal-header">
              <h2>Invoice Preview - {viewingInvoice.invoiceNumber || 'INV-000001'}</h2>
              <button
                className="invoice-modal-close"
                onClick={() => setViewingInvoice(null)}
                title="Close"
              >
                ×
              </button>
            </div>

            {viewingInvoice.pdfDataUrl && (
              <iframe
                src={viewingInvoice.pdfDataUrl}
                title={`Invoice ${viewingInvoice.invoiceNumber}`}
                className="invoice-modal-frame"
              />
            )}

            <div className="invoice-modal-actions">
              <button
                className="invoice-modal-btn primary"
                onClick={() => {
                  handleDownloadInvoice(viewingInvoice);
                  setViewingInvoice(null);
                }}
              >
                <FaDownload /> Download Invoice
              </button>
              <button className="invoice-modal-btn secondary" onClick={() => setViewingInvoice(null)}>
                Close
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default SubscriptionsManagement;
